import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { SessionResponse } from "@velivo/shared";
import { AuthLayout } from "../components/AuthLayout";
import { Field } from "../components/Field";
import { useAuth } from "../auth/AuthContext";
import { api, ApiRequestError } from "../api/client";

/**
 * FR-AUTH-01 / DECISIONS.md AUTH-001 — one call creates the User, the
 * Organization, and the founding OrganizationAdmin grant.
 */
export function SignupPage() {
  const { applySession } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    organizationName: "",
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function update(key: keyof typeof form) {
    return (event: { target: { value: string } }) =>
      setForm((prev) => ({ ...prev, [key]: event.target.value }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setSubmitting(true);
    try {
      const session = await api<SessionResponse>("/auth/signup", {
        method: "POST",
        body: form,
        auth: false,
      });
      applySession(session);
      navigate("/", { replace: true });
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        const fields = caught.fieldErrors();
        if (Object.keys(fields).length > 0) setFieldErrors(fields);
        else setError(caught.message);
      } else {
        setError(
          "Could not reach the server. Check your connection and try again.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Mirrors the server's ALLOW_SELF_SIGNUP. A bookmarked /signup would
  // otherwise render a complete form that can only ever 403 on submit.
  const [allowed, setAllowed] = useState<boolean | null>(null);
  useEffect(() => {
    void api<{ selfSignupEnabled: boolean }>("/auth/config", { auth: false })
      .then((c) => setAllowed(c.selfSignupEnabled))
      .catch(() => setAllowed(false));
  }, []);

  if (allowed === null) return null;
  if (!allowed) {
    return (
      <AuthLayout
        title="Echo is invite-only"
        subtitle="Accounts are created by invitation, not sign-up."
        footer={<Link to="/login">Back to sign in</Link>}
      >
        <p style={{ color: "var(--color-text-secondary)" }}>
          Ask your administrator to invite you. The invitation arrives by email
          and sets up your account and its access at the same time.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="You'll become the first Organization Admin."
      footer={
        <>
          Already have an account? <Link to="/login">Sign in</Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        {error && (
          <div className="alert alert--error" role="alert">
            {error}
          </div>
        )}

        <Field
          label="Your name"
          name="name"
          autoComplete="name"
          value={form.name}
          onChange={update("name")}
          error={fieldErrors.name}
          required
        />
        <Field
          label="Work email"
          type="email"
          name="email"
          autoComplete="email"
          value={form.email}
          onChange={update("email")}
          error={fieldErrors.email}
          required
        />
        <Field
          label="Organization name"
          name="organizationName"
          value={form.organizationName}
          onChange={update("organizationName")}
          help="Your company, or your agency if you manage brands for clients."
          error={fieldErrors.organizationName}
          required
        />
        <Field
          label="Password"
          type="password"
          name="password"
          autoComplete="new-password"
          value={form.password}
          onChange={update("password")}
          help="At least 12 characters."
          error={fieldErrors.password}
          required
        />

        <button
          type="submit"
          className="btn btn--primary btn--block"
          disabled={submitting}
        >
          {submitting ? "Creating account…" : "Create account"}
        </button>
      </form>
    </AuthLayout>
  );
}
